// Libs
import { useCallback, useMemo, useState } from 'react';

// Constants
import { NOTIFICATIONS_ENTITY_TYPE } from '@/common/constants';
import { useNotificationsContext } from '@/common/contexts/NotificationsContext/NotificationsContext';

const useNotifications = ({
  disabled,
  isLoading,
  notifications,
  onUpdateNotifications,
}) => {
  const { setSelectedTypeContext } = useNotificationsContext();
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(
    NOTIFICATIONS_ENTITY_TYPE.ALL,
  );

  const isDisabled = useMemo(
    () => ({
      loading: isLoading,
      disabled: isLoading || disabled,
    }),
    [disabled, isLoading],
  );

  const onToggleMoreActions = useCallback(
    () => setIsMoreActionsOpen(!isMoreActionsOpen),
    [isMoreActionsOpen],
  );

  const onSelectedType = useCallback(
    (e) => {
      setSelectedType(e.target.value);
      setSelectedTypeContext(e.target.value);
    },
    [setSelectedTypeContext],
  );

  /**
   * Function used when the user clicks on read or archive
   * @param {string} type ('read', 'archive')
   * @param {array} notificationIds
   * @returns {*[]}
   */
  const onRead = useCallback(
    (type, notificationIds) => {
      onUpdateNotifications({
        values: {
          type,
          notificationIds,
        },
      });
    },
    [onUpdateNotifications],
  );

  /**
   * Memoize all the notifications which are not read into an array, array is sent to the api to update all the notifications' read status
   * @param {object} notifications
   * @returns {*[]}
   */
  const notificationsNotReaded = useMemo(
    () =>
      notifications?.data
        .filter((item) => !item.is_read)
        .map((item) => item.id),
    [notifications],
  );

  /**
   * Memoize all the notifications which are not archived into an array, array is sent to the api to update all the notifications' archive status
   * @param {object} notifications
   * @returns {*[]}
   */
  const notificationsNotArchived = useMemo(
    () =>
      notifications?.data
        .filter((item) => !item.is_archived)
        .map((item) => item.id),
    [notifications],
  );

  /**
   * Generate more actions classNames
   * @type {string}
   */
  const getMoreActionsClassNames = useMemo(
    () =>
      [
        'button--actions',
        'more-actions__button',
        isMoreActionsOpen ? 'active' : null,
      ]
        .filter((val) => val)
        .join(' '),
    [isMoreActionsOpen],
  );

  /**
   * Generate button classNames
   * @param {string} type
   * @type {string}
   */
  const onGetButtonClassNames = useCallback(
    (type) =>
      ['button--notifications', selectedType === type ? 'active' : null]
        .filter((val) => val)
        .join(' '),
    [selectedType],
  );

  return {
    getMoreActionsClassNames,
    isDisabled,
    isMoreActionsOpen,
    notificationsNotReaded,
    notificationsNotArchived,
    onGetButtonClassNames,
    onRead,
    onSelectedType,
    onToggleMoreActions,
    selectedType,
    setIsMoreActionsOpen,
    setSelectedType,
  };
};

export default useNotifications;
